import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiV1Url } from '../config/api';
import './VoluntaryEntityCreateModal.css';

export type VoluntaryEntityType = 'manufacturer' | 'laboratory' | 'os';

type VoluntaryEntityCreateModalProps = {
  isOpen: boolean;
  entityType: VoluntaryEntityType;
  prefillName?: string;
  osRalStatusOptions?: string[];
  labRalStatusOptions?: string[];
  onClose: () => void;
  onCreated: (entityType: VoluntaryEntityType, data: Record<string, unknown>) => void;
};

const titles: Record<VoluntaryEntityType, string> = {
  manufacturer: 'Добавить производителя',
  laboratory: 'Добавить лабораторию',
  os: 'Добавить орган по сертификации',
};

const authHeaders = () => {
  const access = localStorage.getItem('access');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
};

const compactPayload = (payload: Record<string, string>) => {
  const result: Record<string, string> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value.trim()) result[key] = value.trim();
  });
  return result;
};

const VoluntaryEntityCreateModal: React.FC<VoluntaryEntityCreateModalProps> = ({
  isOpen,
  entityType,
  prefillName = '',
  osRalStatusOptions = [],
  labRalStatusOptions = [],
  onClose,
  onCreated,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manufacturerForm, setManufacturerForm] = useState({ manufacturer: '', inn_unp: '' });
  const [laboratoryForm, setLaboratoryForm] = useState({
    laboratory_name: '',
    accreditation_certificate: '',
    laboratory_ral_status: '',
  });
  const [osForm, setOsForm] = useState({
    os_name: '',
    os_code: '',
    os_ral_status: '',
    os_link: '',
    os_address: '',
    accreditation_scope: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSubmitting(false);
    if (entityType === 'manufacturer') {
      setManufacturerForm({ manufacturer: prefillName, inn_unp: '' });
    } else if (entityType === 'laboratory') {
      setLaboratoryForm({ laboratory_name: prefillName, accreditation_certificate: '', laboratory_ral_status: '' });
    } else {
      setOsForm({
        os_name: prefillName,
        os_code: '',
        os_ral_status: '',
        os_link: '',
        os_address: '',
        accreditation_scope: '',
      });
    }
  }, [entityType, isOpen, prefillName]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (entityType === 'manufacturer') {
        const response = await axios.post(
          getApiV1Url('VOLUNTARY_CERTIFICATES_MANUFACTURE_CREATE'),
          compactPayload(manufacturerForm),
          { headers: authHeaders() },
        );
        onCreated(entityType, response.data);
      } else if (entityType === 'laboratory') {
        const response = await axios.post(
          getApiV1Url('VOLUNTARY_CERTIFICATES_LABORATORY_CREATE'),
          compactPayload(laboratoryForm),
          { headers: authHeaders() },
        );
        onCreated(entityType, response.data);
      } else {
        const response = await axios.post(
          getApiV1Url('VOLUNTARY_CERTIFICATES_OS_CREATE'),
          compactPayload(osForm),
          { headers: authHeaders() },
        );
        onCreated(entityType, response.data);
      }
    } catch (err: unknown) {
      const serverData = (err as { response?: { data?: unknown } })?.response?.data;
      setError(serverData ? JSON.stringify(serverData) : 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="voluntary-entity-modal-overlay" onClick={onClose}>
      <div className="voluntary-entity-modal-content" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="voluntary-entity-modal-close" onClick={onClose}>×</button>
        <h3 className="voluntary-entity-modal-title">{titles[entityType]}</h3>

        {error && <div className="voluntary-entity-modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="voluntary-entity-modal-form">
          {entityType === 'manufacturer' && (
            <>
              <div className="form-group">
                <label>Производитель</label>
                <input
                  value={manufacturerForm.manufacturer}
                  onChange={(e) => setManufacturerForm((prev) => ({ ...prev, manufacturer: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>ИНН / УНП</label>
                <input
                  value={manufacturerForm.inn_unp}
                  onChange={(e) => setManufacturerForm((prev) => ({ ...prev, inn_unp: e.target.value }))}
                />
              </div>
            </>
          )}

          {entityType === 'laboratory' && (
            <>
              <div className="form-group">
                <label>Наименование лаборатории</label>
                <input
                  value={laboratoryForm.laboratory_name}
                  onChange={(e) => setLaboratoryForm((prev) => ({ ...prev, laboratory_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Аттестат аккредитации</label>
                <input
                  value={laboratoryForm.accreditation_certificate}
                  onChange={(e) => setLaboratoryForm((prev) => ({ ...prev, accreditation_certificate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Статус лаборатории в РАЛ</label>
                <input
                  list="lab-ral-status-modal-list"
                  value={laboratoryForm.laboratory_ral_status}
                  onChange={(e) => setLaboratoryForm((prev) => ({ ...prev, laboratory_ral_status: e.target.value }))}
                />
                <datalist id="lab-ral-status-modal-list">
                  {labRalStatusOptions.map((opt, i) => (<option key={i} value={opt} />))}
                </datalist>
              </div>
            </>
          )}

          {entityType === 'os' && (
            <>
              <div className="form-group">
                <label>Наименование ОС</label>
                <input
                  value={osForm.os_name}
                  onChange={(e) => setOsForm((prev) => ({ ...prev, os_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Код ОС</label>
                <input
                  value={osForm.os_code}
                  onChange={(e) => setOsForm((prev) => ({ ...prev, os_code: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Статус ОС в РАЛ</label>
                <input
                  list="os-ral-status-modal-list"
                  value={osForm.os_ral_status}
                  onChange={(e) => setOsForm((prev) => ({ ...prev, os_ral_status: e.target.value }))}
                />
                <datalist id="os-ral-status-modal-list">
                  {osRalStatusOptions.map((opt, i) => (<option key={i} value={opt} />))}
                </datalist>
              </div>
              <div className="form-group">
                <label>Ссылка на ОС</label>
                <input
                  value={osForm.os_link}
                  onChange={(e) => setOsForm((prev) => ({ ...prev, os_link: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Адрес ОС</label>
                <input
                  value={osForm.os_address}
                  onChange={(e) => setOsForm((prev) => ({ ...prev, os_address: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Область аккредитации ОС</label>
                <input
                  value={osForm.accreditation_scope}
                  onChange={(e) => setOsForm((prev) => ({ ...prev, accreditation_scope: e.target.value }))}
                />
              </div>
            </>
          )}

          <div className="voluntary-entity-modal-actions">
            <button type="button" className="voluntary-entity-modal-cancel" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VoluntaryEntityCreateModal;
